/**
 * Plant Mapping Controls Component
 * Control panel for plant mapping operations
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Filter,
  RotateCcw
} from 'lucide-react';
import { usePlantMappingStore } from '../../stores/plantMappingStore';
import { DepartmentService } from '../../services/department';
import DateRangeFilter from '../common/DateRangeFilter';

interface PlantMappingControlsProps {
  onCreateZone: () => void;
  canCreateZone?: boolean;
  canEditLayout?: boolean;
  selectedPlantAlv?: string;
  plantOptions?: Array<{ value: string; label: string }>;
  onPlantChange?: (alvId: string | null) => void;
  backgroundOpacityPercent?: number;
  onBackgroundOpacityChange?: (value: number) => void;
  isSavingBackgroundOpacity?: boolean;
  backgroundFitMode?: 'cover' | 'contain' | 'stretch';
  onBackgroundFitModeChange?: (mode: 'cover' | 'contain' | 'stretch') => void;
}

const PlantMappingControls: React.FC<PlantMappingControlsProps> = ({
  onCreateZone,
  canCreateZone = true,
  canEditLayout = false,
  selectedPlantAlv,
  plantOptions,
  onPlantChange,
  backgroundOpacityPercent = 80,
  onBackgroundOpacityChange,
  isSavingBackgroundOpacity = false,
  backgroundFitMode = 'cover',
  onBackgroundFitModeChange
}) => {
  const [showFilters, setShowFilters] = useState(true);
  const [departments, setDepartments] = useState<Array<{ id: number; name: string; code: string }>>([]);

  const {
    positions,
    unpositionedWorkshops,
    filterStatus,
    filterCategory,
    filterDepartment,
    filterDeadlineFrom,
    filterDeadlineTo,
    filterAlvId,
    setFilters
  } = usePlantMappingStore();

  // Load departments on component mount
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const deptOptions = await DepartmentService.getDepartmentOptions();
        setDepartments(deptOptions);
      } catch (error) {
        console.error('Failed to load departments:', error);
      }
    };
    loadDepartments();
  }, []);

  // Filter options
  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'new', label: 'New' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'risk_of_delay', label: 'Risk of Delay' },
    { value: 'fully_implemented', label: 'Fully Implemented' },
    { value: 'closed', label: 'Closed' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'na', label: 'N/A' }
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'EMPTY', label: 'No Category (Empty)' },
    { value: '213050', label: '213050 - Material usage variance' },
    { value: '213100', label: '213100 - Waste and scrap' },
    { value: '213150', label: '213150 - Production scrap receipts' },
    { value: '213200', label: '213200 - Quality control test' },
    { value: '213250', label: '213250 - Discount/purchase rebate (+)' },
    { value: '213350', label: '213350 - Inventory adjustment - Scrap destruction write off' },
    { value: '213400', label: '213400 - Inventory revaluation' },
    { value: '213450', label: '213450 - Other material costs' },
    { value: '213510', label: '213510 - Freight in' },
    { value: '213520', label: '213520 - Premium freight in' },
    { value: '213600', label: '213600 - Custom/duty' },
    { value: '213700', label: '213700 - Tooling charge' },
    { value: '213750', label: '213750 - Tooling depreciation' },
    { value: '221000', label: '221000 - Direct labour' },
    { value: '223000', label: '223000 - Temporary help' },
    { value: '224000', label: '224000 - Subcontracting' },
    { value: '226000', label: '226000 - Subcontracted rework' },
    { value: '231000', label: '231000 - Freight on sale' },
    { value: '232000', label: '232000 - Premium freight out' },
    { value: '241000', label: '241000 - Packaging on purchases and sales' },
    { value: '246000', label: '246000 - Other direct costs of sales' },
    { value: '311100', label: '311100 - POH - Labor and salaries' },
    { value: '311200', label: '311200 - POH - Temporary help' },
    { value: '311300', label: '311300 - POH - Other personnel costs' },
    { value: '311400', label: '311400 - POH - Travel expenses' },
    { value: '312000', label: '312000 - POH - Subcontracting/outside service' },
    { value: '313000', label: '313000 - POH - Professional service' },
    { value: '314000', label: '314000 - POH - Consumable material and supplies' },
    { value: '315000', label: '315000 - POH - Repairs and maintenance' },
    { value: '317000', label: '317000 - POH - Rent machines' },
    { value: '318000', label: '318000 - POH - Rent buildings' },
    { value: '319000', label: '319000 - POH - Utilities' },
    { value: '321000', label: '321000 - POH - Gain(+)loss(-) on disposals of FA' },
    { value: '322000', label: '322000 - POH - Other production cost' },
    { value: '323000', label: '323000 - POH - Insurance' },
    { value: '325000IC', label: '325000IC POH - GPP-process income' },
    { value: '326000IC', label: '326000IC POH - Group income/expense' },
    { value: '328000', label: '328000 - POH - Depreciation tools' },
    { value: '329000', label: '329000 - POH - Deprec production lines and machines' },
    { value: '330000', label: '330000 - POH - Deprec buildings and other non-production related equipment' },
    { value: '331000', label: '331000 - POH - Extra depreciation' },
    { value: '333000', label: '333000 - POH - Transfer to other accounts' },
    { value: '411100', label: '411100 - SG&A - Labor and salaries' },
    { value: '416000', label: '416000 - SG&A - Rent' },
    { value: '418000', label: '418000 - SG&A - IT-expenses' }
  ];

  const departmentOptions = [
    { value: '', label: 'All Departments' },
    ...departments.map(dept => ({
      value: dept.id.toString(),
      label: `${dept.name} (${dept.code})`
    }))
  ];

  // Extract unique ALV IDs from all workshops (filtered by deadline if applicable)
  const alvIdOptions = React.useMemo(() => {
    if (plantOptions && plantOptions.length > 0) {
      return plantOptions;
    }

    const allWorkshops = [
      ...positions.map(p => p.workshop),
      ...unpositionedWorkshops
    ];
    
    // Apply deadline filter to determine which ALV IDs to show
    const filteredWorkshops = allWorkshops.filter(workshop => {
      if (workshop.deadline) {
        if (filterDeadlineFrom && workshop.deadline < filterDeadlineFrom) return false;
        if (filterDeadlineTo && workshop.deadline > filterDeadlineTo) return false;
      } else if (filterDeadlineFrom || filterDeadlineTo) {
        return false;
      }
      return true;
    });
    
    // Get unique ALV IDs
    const uniqueAlvIds = new Set<string>();
    filteredWorkshops.forEach(workshop => {
      if (workshop.alv_id) {
        uniqueAlvIds.add(workshop.alv_id);
      }
    });
    
    // Sort and create options
    const sortedIds = Array.from(uniqueAlvIds).sort();
    return [
      { value: '', label: 'All Plants' },
      ...sortedIds.map(id => ({ value: id, label: id }))
    ];
  }, [positions, unpositionedWorkshops, filterDeadlineFrom, filterDeadlineTo]);


  return (
    <div className="p-4 border-b">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Controls</h3>
      
      {/* Quick Actions */}
      <div className="space-y-3 mb-6">
        {canEditLayout && (
          <>
            <button
              onClick={onCreateZone}
              disabled={!canCreateZone}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded ${
                canCreateZone
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-300 text-gray-600 cursor-not-allowed'
              }`}
            >
              <Plus size={16} />
              Create Zone
            </button>
            {!canCreateZone && (
              <p className="text-xs text-gray-500">
                Layout is required before creating zones.
              </p>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Layout Opacity
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round(backgroundOpacityPercent)}
                onChange={(e) => onBackgroundOpacityChange?.(parseInt(e.target.value, 10))}
                disabled={!canCreateZone}
                className="w-full"
              />
              <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                <span>{Math.round(backgroundOpacityPercent)}%</span>
                {isSavingBackgroundOpacity && <span>Saving...</span>}
              </div>
            </div>
          </>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Layout Fit Mode
          </label>
          <select
            value={backgroundFitMode}
            onChange={(e) => onBackgroundFitModeChange?.(e.target.value as 'cover' | 'contain' | 'stretch')}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
          >
            <option value="cover">Cover (Crop)</option>
            <option value="contain">Contain (Full Image)</option>
            <option value="stretch">Stretch</option>
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 mb-2"
        >
          <Filter size={16} />
          Filters
        </button>

        {showFilters && (
          <div className="space-y-3 pl-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilters({ status: e.target.value })}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilters({ category: e.target.value })}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Department
              </label>
              <select
                value={filterDepartment || ''}
                onChange={(e) => setFilters({ department: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {departmentOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Plant
              </label>
              <select
                value={selectedPlantAlv !== undefined ? selectedPlantAlv : (filterAlvId || '')}
                onChange={(e) => {
                  const nextAlv = e.target.value || null;
                  if (onPlantChange) {
                    onPlantChange(nextAlv);
                  }
                  setFilters({ alvId: nextAlv });
                }}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {alvIdOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="pt-2 border-t border-gray-100">
               <label className="block text-xs font-medium text-gray-700 mb-1">
                Deadline
              </label>
              <DateRangeFilter 
                startDate={filterDeadlineFrom || ''}
                endDate={filterDeadlineTo || ''}
                onStartDateChange={(date) => setFilters({ deadlineFrom: date || null })}
                onEndDateChange={(date) => setFilters({ deadlineTo: date || null })}
                onClear={() => setFilters({ deadlineFrom: null, deadlineTo: null })}
                className="w-full"
                label=""
                vertical={true}
              />
            </div>

            <button
              onClick={() => setFilters({ 
                status: '', 
                category: '', 
                department: null,
                deadlineFrom: null,
                deadlineTo: null,
                alvId: null
              })}
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 mt-2"
            >
              <RotateCcw size={12} />
              Clear Filters
            </button>
          </div>
        )}
      </div>


      {/* Instructions */}
      <div className="mt-6 p-3 bg-blue-50 rounded text-xs text-blue-800">
        <h5 className="font-medium mb-1">How to use:</h5>
        <ul className="space-y-1 text-blue-700">
          {canEditLayout && <li>• Drag workshop bubbles to position them</li>}
          <li>• Use mouse wheel to zoom in/out</li>
          <li>• Click and drag to pan the view</li>
          <li>• Click workshops for details</li>
          {canEditLayout && <li>• Create zones to organize areas</li>}
        </ul>
      </div>
    </div>
  );
};

export default PlantMappingControls;
